import type { LessonLog, TestRecord } from '@/types';
import { formatDate } from '@/lib/datetime';

interface ParentLessonListProps {
  lessons: LessonLog[];
  testsByLesson: Record<string, TestRecord[]>;
  emptyMessage?: string;
}

export default function ParentLessonList({
  lessons,
  testsByLesson,
  emptyMessage = '아직 등록된 수업일지가 없어요.',
}: ParentLessonListProps) {
  if (lessons.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-background-300 py-12 text-center text-sm text-foreground-500">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {lessons.map((l) => {
        const d = new Date(l.lesson_date);
        const valid = !Number.isNaN(d.getTime());
        const tests = testsByLesson[l.id] ?? [];
        return (
          <article key={l.id} className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <span className="text-xs font-medium">{valid ? `${d.getMonth() + 1}월` : '-'}</span>
                <span className="text-lg font-bold leading-none">{valid ? d.getDate() : '-'}</span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500">{formatDate(l.lesson_date)}</span>
                  {l.is_absent ? (
                    <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                      <i className="ri-user-unfollow-line"></i>
                      결석
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                      <i className="ri-check-line"></i>
                      출석
                    </span>
                  )}
                  {l.is_makeup && (
                    <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
                      <i className="ri-calendar-schedule-line"></i>
                      보충 수업
                    </span>
                  )}
                </div>

                {l.is_absent && l.make_up_date && (
                  <p className="mt-1.5 flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-calendar-schedule-line"></i>
                    보충 예정 {formatDate(l.make_up_date)}
                  </p>
                )}

                <div className="mt-3 flex flex-col gap-2.5">
                  {l.lesson_scope && (
                    <div>
                      <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                        <i className="ri-book-open-line text-primary-600"></i>
                        수업 범위
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                        {l.lesson_scope}
                      </p>
                    </div>
                  )}
                  {l.next_homework && (
                    <div>
                      <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                        <i className="ri-edit-box-line text-secondary-700"></i>
                        다음 숙제
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                        {l.next_homework}
                      </p>
                    </div>
                  )}
                  {l.homework_check && (
                    <div>
                      <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                        <i className="ri-checkbox-circle-line text-accent-600"></i>
                        숙제 이행검사
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                        {l.homework_check}
                      </p>
                    </div>
                  )}
                  {l.notes && (
                    <div>
                      <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                        <i className="ri-sticky-note-line text-foreground-500"></i>
                        특이사항
                      </p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                        {l.notes}
                      </p>
                    </div>
                  )}
                </div>

                {tests.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5 border-t border-background-200 pt-3">
                    {tests.map((t) => (
                      <span
                        key={t.id}
                        className="flex items-center gap-1 rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-800"
                      >
                        <i className="ri-quill-pen-line"></i>
                        {t.test_scope || '테스트'}
                        {t.difficulty && <span>· 난이도 {t.difficulty}</span>}
                        {t.score && <span className="font-bold">· {t.score}</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}